import type { ReactElement, ReactNode } from 'react';
import { Button, Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { cx } from './cx';
import { EmptyState } from './EmptyState';
import { formatAuditTimestamp } from './format';
import { Icon } from './Icon';
import { StatusPill } from './StatusPill';
import s from './History.module.css';

/** Where a change came from (PRD §17 audit standard). */
export type AuditSource =
  'screen' | 'csv_import' | 'directory_sync' | 'system_rule' | 'email' | 'teams' | 'setup_file';

const SOURCE_LABEL: Record<AuditSource, string> = {
  screen: 'In the app',
  csv_import: 'CSV import',
  directory_sync: 'Directory sync',
  system_rule: 'System rule',
  email: 'Email assistant',
  teams: 'Teams assistant',
  setup_file: 'Starting setup file',
};

export interface HistoryEntry {
  id: string;
  /** ISO timestamp (UTC). */
  at: string;
  actor: { name: string; roles?: string };
  /** Whose behalf the change was made on ("by Sam Ortega for Graham Whitlock"). */
  onBehalfOf?: string | null;
  /** What changed, when the drawer covers more than one field. */
  field?: string;
  /** Value before the change; `null` when it was first set. Left out: the entry reads as one event. */
  before?: string | null;
  after: string;
  reason?: string | null;
  /** Where the change came from. Left out when the history doesn't record it. */
  source?: AuditSource;
}

export interface HistoryDrawerProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Eyebrow: what the history belongs to ("History · PAA-2026-0011"). */
  subject: string;
  /** The value's name ("Total value"). */
  title: string;
  /** Current value with its label ("Current value · Jordan Ellis's line"). */
  current?: { label: string; value: string };
  /** Any order; shown newest first. */
  entries: readonly HistoryEntry[];
  /** Shown above the timeline: the value over time and an "as of" picker for number settings. */
  overview?: ReactNode;
  /** While the entries load: says so instead of "No changes yet". */
  loading?: boolean;
  /** Under the timeline: a way on ("Open in the audit log"). */
  footer?: ReactNode;
}

/** Side drawer with the audit timeline for one value: who, when, for whom, before → after, reason, source. */
export function HistoryDrawer({
  isOpen,
  onOpenChange,
  subject,
  title,
  current,
  entries,
  overview,
  loading = false,
  footer,
}: HistoryDrawerProps): ReactElement {
  const newestFirst = [...entries].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable className={s.overlay}>
      <Modal className={s.drawer}>
        <Dialog className={s.dialog}>
          {({ close }) => (
            <>
              <div className={s.head}>
                <div className={s.headText}>
                  <span className={cx('eyebrow', s.eyebrow)}>{subject}</span>
                  <Heading slot="title" className={cx('disp', s.title)}>
                    {title}
                  </Heading>
                </div>
                <Button className={s.close} onPress={close} aria-label="Close history">
                  <Icon name="close" />
                </Button>
              </div>
              <div className={s.body}>
                {current && (
                  <div className={s.current}>
                    <span className={s.currentLabel}>{current.label}</span>
                    <span className={cx('disp', s.currentValue)}>{current.value}</span>
                  </div>
                )}
                {overview}
                <h3 className={cx('eyebrow', s.sectionLabel)}>Changes · newest first</h3>
                {loading ? (
                  <p className={s.meta}>Loading the history…</p>
                ) : newestFirst.length === 0 ? (
                  <EmptyState
                    variant="first-day"
                    art="calendar"
                    size="sm"
                    headingLevel={4}
                    headline="No changes yet"
                    body="Every change to this value will be listed here, with who made it, when and why."
                  />
                ) : (
                  <ol className={s.list}>
                    {newestFirst.map((entry, index) => (
                      <li key={entry.id} className={cx(s.entry, index === 0 && s.latest)}>
                        {entry.field !== undefined && (
                          <span className={s.field}>{entry.field}</span>
                        )}
                        <span className={s.change}>
                          {entry.before !== undefined && (
                            <>
                              <span>{entry.before ?? 'Not set'}</span>
                              <Icon name="arrowRight" size={14} />
                              <span className="visually-hidden">changed to</span>
                            </>
                          )}
                          <span>{entry.after}</span>
                        </span>
                        <span className={s.who}>
                          {entry.actor.name}
                          {entry.onBehalfOf != null && ` for ${entry.onBehalfOf}`}
                          <span className={s.meta}>
                            {entry.actor.roles !== undefined && ` · ${entry.actor.roles}`} ·{' '}
                            {formatAuditTimestamp(entry.at)}
                          </span>
                        </span>
                        {entry.reason != null && entry.reason !== '' && (
                          <span className={s.reason}>Reason: {entry.reason}</span>
                        )}
                        {entry.source !== undefined && (
                          <StatusPill
                            className={s.source}
                            tone="neutral"
                            size="sm"
                            label={`Source · ${SOURCE_LABEL[entry.source]}`}
                          />
                        )}
                      </li>
                    ))}
                  </ol>
                )}
                {footer !== undefined && <div className={s.footer}>{footer}</div>}
              </div>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
